/**
 * One of an organization's intake forms, as the forms list returns it.
 *
 * `base_form_id` is the form's identity rather than a row id: editing a form
 * inserts a new revision carrying the same value, and it is what the patient-facing
 * intake link points at. It is a plain string and not a UUID — the column is
 * free-form, so a form created through the legacy API can hold a supplied value.
 *
 * `is_generic_form` and `is_primary` are always booleans here. Both are nullable in
 * the database; the API reports an unset flag as false rather than passing the
 * storage detail on.
 */
export interface IntakeFormListItem {
  base_form_id: string
  display_name: string
  is_generic_form: boolean
  enabled: boolean
  is_primary: boolean
  /** The link a patient follows to fill this form in, composed by the backend. */
  intake_url: string
}

/**
 * The definition a new custom form is started from, as the API serves it.
 *
 * `form_template` is an arbitrary SurveyJS document and nothing here reads into
 * it — the box shows it and the operator edits it — so it stays `unknown`.
 */
export interface IntakeFormStarterTemplate {
  form_template: unknown
}

/** One reason a definition was refused, or one caveat about accepting it. */
export interface TemplateIssue {
  code: string
  message: string
}

/**
 * A refusal body. Carries the reasons separately rather than joined so a caller
 * can show them one at a time; `warnings` is present on the refusal that asks for
 * them to be acknowledged.
 */
export interface IntakeFormRefusal {
  message: string
  code: string
  errors?: TemplateIssue[]
  warnings?: TemplateIssue[]
}

export interface CreateIntakeFormRequest {
  display_name: string
  form_template: unknown
  /**
   * Set once the definition's warnings have been shown. A definition carrying any
   * is refused until then, because a stored form is live and cannot be withdrawn.
   */
  acknowledge_warnings?: boolean
}

/**
 * The refusal codes something branches on. A code the API can return but that no
 * caller treats specially is deliberately absent — every named refusal is shown
 * in the API's own words, so listing one here without a branch reads as
 * load-bearing to the next person adding one.
 */
export const INTAKE_FORM_ERROR_CODES = {
  DUPLICATE_FORM_NAME: 'duplicate_form_name',
  UNACKNOWLEDGED_WARNINGS: 'unacknowledged_warnings',
} as const

/**
 * The text on a generic form a practice can have changed, as the API serves it.
 *
 * The current values — what a patient opening the form is being shown — rather
 * than what the form was seeded with.
 */
export interface GenericFormText {
  title: string
  intro_text: string
  completion_text: string
  /** The revision these values were read off, sent back on save. */
  form_version: number
}

/**
 * New text for a generic form. All three are sent on every save: the request
 * replaces the current text rather than patching part of it.
 */
export type UpdateGenericFormTextRequest = GenericFormText

/** The form's list row after a text edit, plus the revision it now serves. */
export interface UpdatedGenericForm extends IntakeFormListItem {
  form_version: number
}

/** The stored form, plus the caveats its definition was accepted with. */
export interface CreatedIntakeForm extends IntakeFormListItem {
  warnings: TemplateIssue[]
}

/**
 * Matches the API's own bound. Wider than a location or organization name because
 * a form name is a sentence: the seeded generic form is named
 * "{organization} Patient Intake", which reaches 50 at the organization's limit.
 */
export const INTAKE_FORM_NAME_MAX_LENGTH = 100

/**
 * Matches the API's own bound on the generic form's intro and completion text.
 * Wide because both are prose a patient reads rather than a label, and bounded
 * because both are stored in the definition served on every form load.
 */
export const INTAKE_FORM_TEXT_MAX_LENGTH = 1000
